import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { useAuth } from '../../contexts/AuthContext';

interface UpdateProfileData {
  name: string;
}

interface ChangePasswordData {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

/**
 * Hook to update user profile (name)
 */
export function useUpdateProfile() {
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProfileData) => {
      const response = await apiClient.patch('/api/v1/user/profile', data);
      return response.data;
    },
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

/**
 * Hook to change user password
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: ChangePasswordData) => {
      const response = await apiClient.post('/api/v1/user/change-password', data);
      return response.data;
    },
  });
}

/**
 * Hook to delete user account
 */
export function useDeleteAccount() {
  const { logout } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete('/api/v1/user');
      return response.data;
    },
    onSuccess: async () => {
      // Account is deleted, logout will clear local state
      await logout();
    },
  });
}

/**
 * Hook to update notification preferences
 */
export function useUpdatePreferences() {
  const { refreshUser } = useAuth();

  return useMutation({
    mutationFn: async (prefs: { weekly_digest?: boolean; marketing_emails?: boolean; dismissed_anniversary_banner?: boolean }) => {
      const response = await apiClient.patch('/api/v1/user/preferences', prefs);
      return response.data;
    },
    onSuccess: async () => {
      await refreshUser();
    },
  });
}

/**
 * Hook to mark builder tour as completed
 */
export function useCompleteTour() {
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post('/api/v1/user/tour-completed');
      return response.data;
    },
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}
